import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Button, Card, CardContent, Skeleton, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { buildDrillDownHref } from "../dashboard/managementDashboardUtils";
import { formatNumber } from "../../../../../infrastructure/utils/formatters";
import { pick } from "../dashboard/managementDashboardUtils";

const STAGE_TOOLTIPS = {
  Registered: "درخواست‌هایی که در بازه انتخابی ثبت شده‌اند (کوهورت).",
  InformationCompleted: "مرحله بدون منبع کانونیکال — در این نسخه نمایش داده نمی‌شود.",
  InitialReview: "ورود به صف بررسی اولیه: NotChecked یا مرحله ۱.",
  InitiallyApproved: "تکمیل موفق مرحله ۱ (StepInfoId=1, RequestConfirm=Completed).",
  LoanAndMehrsan: "ورود به زیرفرایند وام یا مهرسان (نه الزاماً تکمیل هر دو).",
  SiteVisit: "بازدید محل با وضعیت Accepted.",
  CustomerPriceOffer: "پیشنهاد قیمت مشتری تأیید/ارسال‌شده (نه پیش‌فاکتور پیمانکار).",
  CustomerApproved: "پذیرش مشتری روی پیشنهاد قیمت.",
  CustomerContract: "قرارداد مشتری با ReviewStatus=ApprovedBySolarent.",
  DesignAndProcurement: "برنامه اجرا با وضعیت Approved یا بالاتر.",
  Installation: "احداث نیروگاه با تأیید سولارنت یا ناظر.",
  GridConnection: "اتصال به شبکه با تأیید سولارنت یا ناظر.",
  Commissioned: "پروانه بهره‌برداری با AcceptedBySupervisor.",
};

const FunnelMetricTooltip = ({ stageCode, title }) => (
  <Tooltip title={STAGE_TOOLTIPS[stageCode] || title} arrow placement="top">
    <HelpOutlineIcon fontSize="inherit" sx={{ opacity: 0.7, verticalAlign: "middle" }} />
  </Tooltip>
);

const ApplicationFunnelStage = ({ stage, isLast = false }) => {
  const code = pick(stage, "stageCode", "StageCode");
  const title = pick(stage, "title", "Title");
  const count = pick(stage, "count", "Count");
  const conversion = pick(stage, "conversionRatePercent", "ConversionRatePercent");
  const capacity = pick(stage, "totalCapacityKw", "TotalCapacityKw");
  const isAvailable = pick(stage, "isAvailable", "IsAvailable") !== false;
  const unavailableReason = pick(stage, "unavailableReason", "UnavailableReason");
  const drillDownHref = buildDrillDownHref(pick(stage, "drillDown", "DrillDown"));

  const content = (
    <Card
      variant="outlined"
      sx={{
        minWidth: { xs: 180, md: 150 },
        flex: "1 1 150px",
        opacity: isAvailable ? 1 : 0.72,
        borderColor: isAvailable ? "divider" : "warning.light",
        position: "relative",
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {title}
          <FunnelMetricTooltip stageCode={code} title={title} />
        </Typography>

        {isAvailable ? (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {formatNumber(count ?? 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {conversion == null ? "—" : `${formatNumber(conversion, 2)}٪ تبدیل`}
            </Typography>
            {capacity != null ? (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {formatNumber(capacity)} kW
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="warning.dark" sx={{ mt: 1 }}>
            {unavailableReason || "داده کانونیکال کافی نیست"}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (!isAvailable || !drillDownHref) {
    return (
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, flex: "1 1 auto" }}>
        {content}
        {!isLast ? (
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", color: "text.disabled" }}>
            ←
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, flex: "1 1 auto" }}>
      <Box
        component={RouterLink}
        to={drillDownHref}
        sx={{ textDecoration: "none", color: "inherit", flex: 1 }}
      >
        {content}
      </Box>
      {!isLast ? (
        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", color: "text.disabled" }}>
          ←
        </Box>
      ) : null}
    </Box>
  );
};

const ApplicationFunnelSection = ({
  loading = false,
  error = null,
  stages = [],
  cohortSize = 0,
  excludedCancelledCount = 0,
  onRetry,
}) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        قیف درخواست‌ها (کوهورت)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        درخواست‌های ثبت‌شده در بازه انتخابی و پیشرفت آن‌ها تا امروز.
        {cohortSize ? ` کوهورت: ${formatNumber(cohortSize)}` : ""}
        {excludedCancelledCount ? ` | لغوشده: ${formatNumber(excludedCancelledCount)}` : ""}
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : error ? (
        <Box>
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Button size="small" variant="outlined" onClick={onRetry}>
            تلاش مجدد
          </Button>
        </Box>
      ) : stages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          داده‌ای برای نمایش قیف وجود ندارد.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: { xs: "auto", md: "visible" },
            flexDirection: { xs: "column", md: "row" },
            pb: { xs: 1, md: 0 },
          }}
        >
          {stages.map((stage, index) => (
            <ApplicationFunnelStage
              key={pick(stage, "stageCode", "StageCode") || index}
              stage={stage}
              isLast={index === stages.length - 1}
            />
          ))}
        </Box>
      )}
    </CardContent>
  </Card>
);

export default ApplicationFunnelSection;
